/**
 * The authenticated application shell: sidebar, top bar, content outlet.
 *
 * Navigation is filtered by the user's resolved permissions, so an accountant
 * and a class teacher see genuinely different products from the same build.
 */

import { useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { NotificationBell } from "@/features/notifications";
import type { PortalId } from "@/shared/api/types";
import { useAuth, usePermissions } from "@/shared/auth/hooks";
import { cn } from "@/shared/lib/cn";
import { useT } from "@/shared/i18n";
import { LanguageSwitcher } from "@/shared/i18n/LanguageSwitcher";
import { PORTAL_LABELS, PORTAL_NAV, type NavSection } from "@/shared/navigation/nav.config";
import { Badge, Button, Spinner } from "@/shared/ui";

export function AppShell({ portal }: { portal: PortalId }) {
  const { session, logout } = useAuth();
  const { can, canAny } = usePermissions();
  const navigate = useNavigate();
  const t = useT();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  // Two filters, for two different reasons:
  //   permission — this user may not use the feature
  //   phase      — the feature does not exist yet
  // The second is a development aid only. Schools see a sidebar of things that
  // work, not a preview of our roadmap.
  const sections = useMemo<NavSection[]>(() => {
    const showUnbuilt = import.meta.env.DEV;
    const permitted = (item: NavSection["items"][number]) => {
      if (item.permission && !can(item.permission)) return false;
      // `anyOf` mirrors what the endpoint accepts. Without it a student, who
      // holds only `.self` variants, sees an empty sidebar over data the API
      // would return.
      if (item.anyOf?.length && !canAny(...item.anyOf)) return false;
      return true;
    };
    return PORTAL_NAV[portal]
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => permitted(item) && (showUnbuilt || !item.phase)),
      }))
      .filter((section) => section.items.length > 0);
  }, [portal, can, canAny]);

  if (!session) return null;

  const handleSignOut = async () => {
    setSigningOut(true);
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[16rem_1fr]">
      {/* Skip link — keyboard users should not tab through the whole sidebar. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-[var(--brand)] focus:px-3 focus:py-2 focus:text-white"
      >
        {t("Skip to content")}
      </a>

      <aside
        id="sidebar"
        className={cn(
          "surface border-e lg:sticky lg:top-0 lg:h-dvh lg:overflow-y-auto",
          mobileNavOpen ? "block" : "hidden lg:block",
        )}
      >
        <div className="flex h-16 items-center gap-3 border-b border-[var(--border-subtle)] px-5">
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-[var(--brand)] text-sm font-bold text-white">
            {initials(session.tenant.name)}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">{session.tenant.name}</p>
            <p className="text-muted text-xs">{t(PORTAL_LABELS[portal])}</p>
          </div>
        </div>

        <nav aria-label="Main" className="p-3">
          {sections.map((section) => (
            <div key={section.id} className="mb-4">
              <p className="text-muted px-2 pb-1 text-[0.68rem] font-semibold uppercase tracking-wider">
                {t(section.label)}
              </p>
              <ul className="flex flex-col gap-0.5">
                {section.items.map((item) => (
                  <li key={item.id}>
                    {item.phase ? (
                      // Development only — filtered out above in deployed
                      // environments.
                      <span
                        className="text-muted flex cursor-not-allowed items-center gap-3 rounded-lg px-2 py-2 text-sm opacity-55"
                        title={`${t("Not built yet")} — ${item.phase}`}
                      >
                        <span aria-hidden="true" className="w-4 text-center">
                          {item.icon}
                        </span>
                        <span className="flex-1 truncate">{t(item.label)}</span>
                        <Badge>{item.phase}</Badge>
                      </span>
                    ) : (
                      <NavLink
                        to={item.to}
                        end={item.to.split("/").length <= 2}
                        onClick={() => setMobileNavOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-3 rounded-lg px-2 py-2 text-sm transition-colors",
                            isActive
                              ? "bg-[var(--brand)]/12 font-medium text-[var(--brand)]"
                              : "hover:bg-[var(--surface-muted)]",
                          )
                        }
                      >
                        <span aria-hidden="true" className="w-4 text-center">
                          {item.icon}
                        </span>
                        <span className="truncate">{t(item.label)}</span>
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="surface sticky top-0 z-10 flex h-16 items-center gap-3 border-b px-4 lg:px-6">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            aria-expanded={mobileNavOpen}
            aria-controls="sidebar"
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            <span aria-hidden="true">☰</span>
            <span className="sr-only">{t("Toggle navigation")}</span>
          </Button>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{session.user.full_name}</p>
            <p className="text-muted truncate text-xs">
              {session.roles.map((role) => t(humaniseRole(role))).join(" · ")}
            </p>
          </div>

          {session.tenant.status === "past_due" && (
            <Badge tone="warning">{t("Payment overdue")}</Badge>
          )}

          {/* Present on every screen, which is the point: a message the school
              sent should not need the recipient to go looking for it. */}
          <LanguageSwitcher />

          <NotificationBell inboxPath={`/${portal === "parent" ? "family" : portal}/notifications`} />

          <Button variant="secondary" size="sm" onClick={handleSignOut} disabled={signingOut}>
            {signingOut ? <Spinner className="size-4" /> : t("Sign out")}
          </Button>
        </header>

        <main id="main" className="min-w-0 flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

function humaniseRole(role: string): string {
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
